import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Api, isProcessingStatus, normalizeStatus } from '../lib';

/**
 * Poll subscription status when in PROCESSING state and notify on status change
 * @param {Object} subscription - Current subscription object
 * @param {Function} onStatusChange - Callback when status changes
 * @param {number} intervalMs - Polling interval in milliseconds (default: 3000)
 */
export default function useSubscriptionPolling(subscription, onStatusChange, intervalMs = 3000) {
  const intervalRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    // Only poll when subscription is in PROCESSING or INITIATED state
    const shouldPoll = isProcessingStatus(subscription);

    if (shouldPoll && subscription?.subscriptionId) {
      intervalRef.current = setInterval(async () => {
        try {
          const res = await Api.get('/subscriptionPlans/status');
          
          if (res?.data) {
            const items = Array.isArray(res.data) ? res.data : [res.data];
            const updated = items.find(s => s.subscriptionId === subscription.subscriptionId);
            
            if (updated && updated.status !== subscription.status) {
              // Status changed - stop polling and notify
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
              
              // Show success/failure message
              const normalizedStatus = normalizeStatus(updated.status);
              if (normalizedStatus === 'ACTIVE' || normalizedStatus === 'SUCCESS') {
                dispatch({
                  type: 'HANDLE_RES',
                  payload: { res: 'Payment processed successfully!' }
                });
              } else if (normalizedStatus === 'FAILED') {
                dispatch({
                  type: 'HANDLE_RES',
                  payload: { err: 'Payment failed. Please try again.' }
                });
              } else if (normalizedStatus === 'RETRYING') {
                dispatch({
                  type: 'HANDLE_RES',
                  payload: { err: 'Payment failed. Your payment method will be retried.' }
                });
              }
              
              // Trigger callback
              if (onStatusChange) {
                onStatusChange(updated);
              }
            }
          }
        } catch (error) {
          console.error('Error polling subscription status:', error);
          // Don't stop polling on error - might be temporary network issue
        }
      }, intervalMs);
    }

    // Cleanup on unmount or when polling stops
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [subscription?.status, subscription?.subscriptionId, onStatusChange, intervalMs, dispatch]);

  return null;
}
